import styled from '@emotion/styled';
import { CloseRounded } from '@mui/icons-material';
import { shuffle } from 'es-toolkit';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { SongPreview } from '~/interfaces';
import { useBackground } from '~/modules/Elements/BackgroundContext';
import { Button } from '~/modules/Elements/Button';
import NoPrerender from '~/modules/Elements/NoPrerender';
import SmoothLink from '~/modules/Elements/SmoothLink';
import VideoPlayer, { VideoState } from '~/modules/Elements/VideoPlayer';
import useSongIndex from '~/modules/Songs/hooks/useSongIndex';
import useBackgroundMusic from '~/modules/hooks/useBackgroundMusic';
import useKeyboardNav from '~/modules/hooks/useKeyboardNav';
import useSmoothNavigate, { buildUrl } from '~/modules/hooks/useSmoothNavigate';
import LayoutGame from '~/routes/LayoutGame';
import SongPage from '../Game/SongPage';

function Jukebox() {
  useBackground(false);
  useBackgroundMusic(false);
  const navigate = useSmoothNavigate();
  const [currentlyPlaying, setCurrentlyPlaying] = useState(0);
  const [playerSize, setPlayerSize] = useState({ width: 0, height: 0 });
  const viewport = useRef<HTMLDivElement>(null);
  const songList = useSongIndex();

  const [shuffledList, setShuffledList] = useState<SongPreview[]>([]);
  const exitJukebox = () => navigate('');
  const { register } = useKeyboardNav({ onBackspace: exitJukebox });

  useEffect(() => songList.data && setShuffledList(shuffle(songList.data)), [songList.data]);

  useLayoutEffect(() => {
    const element = viewport.current;
    if (!element) return;

    let parent = element.parentElement;
    let scrollContainer: HTMLElement | null = null;
    while (parent) {
      const overflowY = getComputedStyle(parent).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        scrollContainer = parent;
        break;
      }
      parent = parent.parentElement;
    }

    const resetScroll = () => scrollContainer?.scrollTo({ top: 0, left: 0 });
    resetScroll();
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(resetScroll);
    });
    const delayedReset = window.setTimeout(resetScroll, 200);

    const updateSize = () => {
      const bounds = element.getBoundingClientRect();
      setPlayerSize({ width: Math.floor(bounds.width), height: Math.floor(bounds.height) });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
      window.clearTimeout(delayedReset);
    };
  }, []);

  const playNext = () => shuffledList.length && setCurrentlyPlaying((current) => (current + 1) % shuffledList.length);

  const currentSong = shuffledList[currentlyPlaying];
  const navigateUrl = currentSong ? buildUrl(`game/`, { song: currentSong.id, playlist: 'All' }) : '';

  return (
    <LayoutGame>
      <Helmet>
        <title>Jukebox | fastKaraoke</title>
      </Helmet>
      <NoPrerender>
        <JukeboxViewport ref={viewport}>
          {currentSong && playerSize.width > 0 && playerSize.height > 0 && (
            <SongPage
              width={playerSize.width}
              height={playerSize.height}
              songData={currentSong}
              data-test="jukebox-container"
              data-song={currentSong.id}
              background={
                <VideoPlayer
                  autoplay
                  controls
                  width={playerSize.width}
                  height={playerSize.height}
                  volume={currentSong.volume}
                  video={currentSong.video}
                  startAt={currentSong.videoGap}
                  onStateChange={(state) => {
                    if (state === VideoState.ENDED) playNext();
                  }}
                />
              }>
              <ExitButton
                {...register('exit-button', exitJukebox)}
                aria-label="Close Jukebox"
                title="Back to song list">
                <CloseRounded />
              </ExitButton>
              <SkipSongButton {...register('skip-button', playNext)}>Skip</SkipSongButton>
              <SmoothLink to={navigateUrl}>
                <PlayThisSongButton {...register('sing-button', () => navigate(navigateUrl), undefined, true)}>
                  Sing this song
                </PlayThisSongButton>
              </SmoothLink>
            </SongPage>
          )}
        </JukeboxViewport>
      </NoPrerender>
    </LayoutGame>
  );
}

const JukeboxViewport = styled.div`
  position: relative;
  width: 100%;
  height: var(--karaoke-content-height, calc(100dvh - 152px));
  min-height: 0;
  overflow: hidden;
  background: black;
`;

const PlayThisSongButton = styled(Button)<{ focused: boolean }>`
  bottom: 56px;
  right: 16px;
  width: min(420px, calc(100% - 32px));
  position: absolute;
  z-index: 5;
  padding: 10px 18px;
  font-size: clamp(18px, 1.7vw, 28px);
  border-radius: 8px;

  @media (max-width: 640px) {
    right: 12px;
    bottom: 58px;
    width: calc(100% - 24px);
    font-size: 18px;
  }
`;

const SkipSongButton = styled(Button)<{ focused: boolean }>`
  bottom: 116px;
  right: 16px;
  width: min(240px, calc(100% - 32px));
  position: absolute;
  z-index: 5;
  padding: 9px 16px;
  font-size: clamp(16px, 1.35vw, 22px);
  border-radius: 8px;

  @media (max-width: 640px) {
    right: 12px;
    bottom: 112px;
    width: min(180px, calc(100% - 24px));
    font-size: 16px;
  }
`;

const ExitButton = styled(Button)<{ focused: boolean }>`
  position: absolute;
  z-index: 6;
  top: 16px;
  right: 16px;
  width: 48px;
  height: 48px;
  min-width: 48px;
  padding: 0;
  border-radius: 50%;
  background: rgba(34, 34, 34, 0.9);
  color: white;

  svg {
    width: 26px;
    height: 26px;
  }

  @media (max-width: 640px) {
    top: 12px;
    right: 12px;
    width: 44px;
    height: 44px;
    min-width: 44px;
  }
`;

export default Jukebox;
